/**
 *  ActivationSettingsExterior script file
**/

import authService, { USER_ROLES } from '@/api/authService'
import campaignsService from '@/api/campaignsService'
import campaignsUtils from '@/mixins/campaignsUtils'
import { CAMPAIGN_STATUSES } from '@/utils/campaignOptions'

export default {
  name: 'ActivationSettingsExterior',
  mixins: [campaignsUtils],
  components: {
    CampaignComments: () =>
      import('@/components/campaign-comments/CampaignComments.vue')
  },
  props: {
    campaign: {
      type: Object,
      required: true
    },
    comments: {
      type: Array,
      required: true
    },
    lastCentralCommentStatus: {
      type: String | undefined,
      required: true
    },
    lastDirectorCommentStatus: {
      type: String | undefined,
      required: true
    }
  },
  mounted () {
    this.centralStatus = this.lastCentralCommentStatus
    this.directorStatus = this.lastDirectorCommentStatus
  },
  data () {
    return {
      comment: '',
      isAccepted: null,
      authData: authService.authData,
      loading: false,
      isDeleting: false,
      centralStatus: undefined,
      directorStatus: undefined
    }
  },
  computed: {
    isValidComment () {
      return this.comment.replace(/\s/g, '').length !== 0
    },
    isDirector () {
      return this.authData.user.role === USER_ROLES.DIRECTOR
    },
    isCentral () {
      return this.authData.user.role === USER_ROLES.CENTRAL_ADMIN || this.authData.user.role === USER_ROLES.CENTRAL || this.authData.user.role === USER_ROLES.ROOT
    },
    isRejected () {
      return (this.campaign && this.campaign.status === CAMPAIGN_STATUSES.CENTRAL_REJECTED) || this.isStatusRejected
    },
    ignorePreviousStatus () {
      return this.campaign.status === CAMPAIGN_STATUSES.CENTRAL_READY
    },
    directorRejected () {
      return this.metadataStatus === CAMPAIGN_STATUSES.DIRECTOR_REJECTED
    },
    statusTextColor () {
      const map = {
        [CAMPAIGN_STATUSES.DIRECTOR_REJECTED]: 'var(--colorFeedError)',
        [CAMPAIGN_STATUSES.DIRECTOR_ACCEPTED]: 'var(--colorFeedOk)'
      }
      return map[this.metadataStatus]
    },
    confirmButtonText () {
      if (this.isDirector && this.campaign.status === CAMPAIGN_STATUSES.CENTRAL_ACCEPTED) {
        return 'Confirmar y enviar a central'
      }
      return this.isStatusAccepted
        ? 'Confirmar y activar campaña'
        : 'Confirmar y enviar'
    },
    isStatusAccepted () {
      return this.centralStatus === CAMPAIGN_STATUSES.CENTRAL_ACCEPTED && this.directorStatus === CAMPAIGN_STATUSES.DIRECTOR_ACCEPTED
    },
    isStatusRejected () {
      return this.centralStatus === CAMPAIGN_STATUSES.CENTRAL_REJECTED && this.directorStatus === CAMPAIGN_STATUSES.DIRECTOR_REJECTED
    },
    showValidationButtons () {
      // No matched status: string, or initial validation status: both are undefined
      return this.centralStatus !== this.directorStatus || (this.centralStatus === undefined && this.directorStatus === undefined)
    },
    isValidationMatched () {
      return this.centralStatus === this.directorStatus
    }
  },
  methods: {
    accept () {
      this.isAccepted = true
    },
    reject () {
      this.isAccepted = false
    },
    back () {
      this.$emit('back')
    },
    async confirm () {
      this.loading = true
      const statuses = this.isDirector
        ? [CAMPAIGN_STATUSES.DIRECTOR_ACCEPTED, CAMPAIGN_STATUSES.DIRECTOR_REJECTED]
        : [CAMPAIGN_STATUSES.CENTRAL_ACCEPTED, CAMPAIGN_STATUSES.CENTRAL_REJECTED]

      if (this.isAccepted !== null || this.isStatusAccepted) {
        let newStatus = this.isAccepted || this.isStatusAccepted ? statuses[0] : statuses[1]

        await campaignsService.updateStatus({
          id: this.campaign.id,
          status: newStatus,
          comment: this.comment || undefined
        })

        if (this.isDirector) {
          this.campaign.metadata = {
            exterior_director_status: newStatus,
            exterior_director_comment: this.comment
          }
          this.directorStatus = newStatus
        } else {
          if (newStatus === CAMPAIGN_STATUSES.CENTRAL_ACCEPTED) {
            newStatus = CAMPAIGN_STATUSES.AGENCY_CONFIRMED
          }
          this.centralStatus = newStatus
          this.campaign.status = newStatus
          this.campaign.status_comment = this.comment
          this.isAccepted = null
        }

        this.$emit('edit', this.campaign)
        await this.$router.push('/campaigns')
      }
      this.loading = false
    },
    async remove () {
      if (!this.isDeleting) {
        this.isDeleting = true
        return
      }

      await campaignsService.deleteCampaign({ id: this.campaign.id })
      this.$emit('delete', this.campaign.id)
      this.$router.push('/campaigns')
    },
    async setProvisioning () {
      try {
        const nextStatus = CAMPAIGN_STATUSES.AGENCY_PROVISIONING
        await campaignsService.updateStatus({
          id: this.campaign.id,
          status: nextStatus
        })
        this.campaign.status = nextStatus
        this.$emit('updateTab', { id: 'info' })
      } catch (err) {
        // eslint-disable-next-line
        console.error('[activation-settings-exterior.js] Error setting campaign to draft', err)
      }
    }
  }
}
